import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, categories } from "@/mocks/news";

const PAGE_SIZE = 6;

const filters = [
  { slug: "all", name: "综合" },
  ...categories.map((category) => ({ slug: category.slug, name: category.name })),
];

export default function FeedSection() {
  const [activeSlug, setActiveSlug] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return articles.filter((article) => {
      const matchCategory = activeSlug === "all" || article.categorySlug === activeSlug;
      const matchKeyword =
        !key ||
        article.title.toLowerCase().includes(key) ||
        article.excerpt.toLowerCase().includes(key) ||
        article.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchCategory && matchKeyword;
    });
  }, [activeSlug, keyword]);

  const visible = filtered.slice(0, visibleCount);

  const handleFilter = (slug: string) => {
    setActiveSlug(slug);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <section id="feed" className="mx-auto max-w-content scroll-mt-32 px-4 pt-16 md:px-6 md:pt-20">
      <SectionHeading
        eyebrow="Latest"
        title="最新吃瓜 · 按频道筛选你想看的"
        description="支持按频道切换与关键词搜索。找不到想看的方向，试试直接搜艺人化名或事件关键词。"
      />

      <div className="mt-7 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="频道筛选">
          {filters.map((item) => (
            <button
              key={item.slug}
              type="button"
              role="tab"
              aria-selected={activeSlug === item.slug}
              onClick={() => handleFilter(item.slug)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors ${
                activeSlug === item.slug
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>

        <div className="relative w-full lg:w-72">
          <i
            className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={keyword}
            onChange={(event) => {
              setKeyword(event.target.value);
              setVisibleCount(PAGE_SIZE);
            }}
            placeholder="搜索标题、标签或事件关键词"
            aria-label="搜索文章"
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30"
          />
        </div>
      </div>

      {visible.length > 0 ? (
        <>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>

          {visibleCount < filtered.length ? (
            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                显示更多内容
                <i className="ri-arrow-down-line" aria-hidden="true" />
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
          </div>
          <p className="mt-4 font-heading text-base font-bold text-foreground-900">没有找到匹配的内容</p>
          <p className="mt-2 text-sm text-foreground-600">
            换个关键词试试，或点击下方按钮查看全部内容。
          </p>
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              handleFilter("all");
            }}
            className="mt-5 inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </section>
  );
}